import { useEffect, useRef } from "react";
import { useIsFetching } from "@tanstack/react-query";

const EVENT_NAME = "prerender-ready";
// Minimum time from mount before we're willing to fire the event — gives pages
// a chance to register their initial React Query queries before we check for
// "idle" state. Without this, pages that fetch on mount could fire the event
// BEFORE the first query even kicks off.
const MIN_MOUNT_DELAY_MS = 1500;
// Debounce window: React Query must be idle for this long before we fire.
const IDLE_DEBOUNCE_MS = 350;

declare global {
    interface Window {
        __prerenderReadyFired?: boolean;
    }
}

/**
 * Fire a single `prerender-ready` event once React Query has settled and the
 * page has had enough time for initial queries to register. Puppeteer (via
 * the @prerenderer plugin's `renderAfterDocumentEvent` option) waits for this
 * event before capturing HTML so per-route meta tags + content are guaranteed
 * to be rendered.
 *
 * Safe in production: the event has no listeners outside the prerender server
 * so it's a no-op for real users. The `__prerenderReadyFired` guard ensures we
 * only dispatch once per tab load.
 */
export function usePrerenderReady(): void {
    const isFetching = useIsFetching();
    const mountedAt = useRef<number>(0);

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (!mountedAt.current) mountedAt.current = performance.now();
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (window.__prerenderReadyFired) return;
        if (isFetching > 0) return;

        const elapsed = performance.now() - (mountedAt.current || performance.now());
        const waitMs = Math.max(MIN_MOUNT_DELAY_MS - elapsed, IDLE_DEBOUNCE_MS);

        const timer = setTimeout(() => {
            if (window.__prerenderReadyFired) return;
            window.__prerenderReadyFired = true;
            requestAnimationFrame(() => {
                document.dispatchEvent(new Event(EVENT_NAME));
            });
        }, waitMs);

        return () => clearTimeout(timer);
    }, [isFetching]);
}
